import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { AmlRiskLevel, getAml, postAml } from '@/services/api/profile';
import { userAttributesQueryKey } from './use-user-attributes';

const profileAmlQueryKey = (pid: UserPid) => ['profile', 'aml', pid];

export const profileAmlQueryOptions = (pid: UserPid) =>
  queryOptions({
    queryFn: getAml,
    queryKey: profileAmlQueryKey(pid),
  });

const useProfileAml = () => {
  const { pid } = useUserPid();
  return useQuery(profileAmlQueryOptions(pid));
};

const useProfileAmlEligibility = () => {
  const { data, ...rest } = useProfileAml();

  return {
    ...rest,
    data: typeof data?.riskLevel === 'number' ? data.riskLevel !== AmlRiskLevel.PROHIBITED : null,
  };
};

const useProfileAmlMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postAml,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: profileAmlQueryKey(pid) });
      queryClient.invalidateQueries({ queryKey: userAttributesQueryKey(pid) });
    },
  });
};

export { useProfileAml, useProfileAmlEligibility, useProfileAmlMutation };
